import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchOrdersByDate,fetchSalesOrderById } from "../../Redux/slice/orderSlice";
import {
  DatePicker,
  Button,
  Table,
  message,
  Empty,
  Input,
  Tag,
  Tooltip,
  Checkbox 
} from "antd";
import { EyeOutlined } from "@ant-design/icons";
import UpdateOrderCycleModal from "./UpdateOrderCycleModal";
import OrderDetailsModal from "./OrderDetailsModal";
import spin from "../../assets/load.gif"
import * as XLSX from "xlsx";

const { RangePicker } = DatePicker;
const { Search } = Input;

const Orders = () => {
  const dispatch = useDispatch();
  const { orders = [], loading } = useSelector((state) => state.orders);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrderCycle, setSelectedOrderCycle] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [cachedOrderDetails, setCachedOrderDetails] = useState({});

  const fetchCurrentMonthOrders = useCallback(() => {
    const currentDate = new Date(); // Today's date
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1); // Start of the month
    const nextDay = new Date(currentDate);
    nextDay.setDate(nextDay.getDate() + 1); // A day after the current date
  
    const from = startOfMonth.toISOString().split("T")[0];
    const to = nextDay.toISOString().split("T")[0];
  
    dispatch(fetchOrdersByDate({ from, to }));
  }, [dispatch]);
  
  

  const handleDateChange = (dates) => {
    if (dates) {
      setDateRange(dates);
    } else {
      setDateRange([null, null]);
    }
  };

  const handleFetchOrders = () => {
    if (dateRange[0] && dateRange[1]) {
      const from = dateRange[0].format("YYYY-MM-DD");
      const to = dateRange[1].add(1, "day").format("YYYY-MM-DD");
      dispatch(fetchOrdersByDate({ from, to }));
    } else {
      message.error("Please select a date range");
    }
  };

  const handleSearch = (value) => {
    setSearchText(value.toLowerCase());
    setPagination({ ...pagination, current: 1 });
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  useEffect(() => {
    fetchCurrentMonthOrders();
  }, [fetchCurrentMonthOrders]);

  const openCycleModal = (order) => {
    setSelectedOrderId(order.orderCode);
    setSelectedOrderCycle(order.orderCycle);
    setIsModalOpen(true);
  };
  const handleSelectAll = () => {
    const newCheckedState = !selectAll;
    setSelectAll(newCheckedState);
    const updatedCheckboxes = {};
    groupedOrders.forEach((order) => {
      updatedCheckboxes[order.orderCode] = newCheckedState;
    });
    setSelectedCheckboxes(updatedCheckboxes);
  };
  const handleCheckboxClick = (orderCode) => {
    setSelectedCheckboxes((prev) => ({
      ...prev,
      [orderCode]: !prev[orderCode], // Toggle checkbox selection
    }));
  };

  const openDetailModal = async (orderId) => {
    if (!cachedOrderDetails[orderId]) {
      const orderDetails = await fetchSalesOrderById(orderId);
      setCachedOrderDetails((prev) => ({ ...prev, [orderId]: orderDetails }));
    }
    setSelectedOrderId(orderId);
    setIsDetailModalOpen(true);

    // Select checkbox when opening the details
    handleCheckboxClick(orderId);
  };


  const groupedOrders = Object.values(
    (orders || []).reduce((acc, order) => {
      if (!acc[order.orderCode]) {
        acc[order.orderCode] = { ...order, orders: [order] };
      } else {
        acc[order.orderCode].orders.push(order);
      }
      return acc;
    }, {})
  );

  const filteredOrders = groupedOrders
    .filter((order) => {
      if (!order) return false;
      const fullNameMatch =
        order.fullName && order.fullName.toLowerCase().includes(searchText);
      const statusMatch =
        order.orderCycle && order.orderCycle.toLowerCase().includes(searchText);
      return fullNameMatch || statusMatch;
    })
    .sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));

    const exportToExcel = () => {
      if (!filteredOrders.length) {
        message.warning("No orders available to export");
        return;
      }
    
      const formattedData = filteredOrders.map((order) => ({
        "Order Code": order.orderCode,
        "Order Date": order.orderDate,
        "Full Name": order.fullName,
        "Contact Number": order.contactNumber,
        "Payment Mode": order.paymentMode || "N/A",
        Status: order.orderCycle,
      }));
    
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");
    
      XLSX.writeFile(workbook, "Orders.xlsx");
    };
  const columns = [
    {
      title: (
        <div className="flex items-center">
          <Checkbox checked={selectAll} onChange={handleSelectAll} />
          <span className="ml-2">Select</span>
        </div>
      ),
      key: "select",
      render: (_, record) => (
        <Checkbox
          checked={selectedCheckboxes[record.orderCode] || false}
          onChange={() => handleCheckboxClick(record.orderCode)}
        />
      ),
    },
    {
      title: "Order Code",
      dataIndex: "orderCode",
      key: "orderCode",
  
    },
    {
      title: "Order Date",
      dataIndex: "orderDate",
      key: "orderDate",
      render: (date) => {
        if (date === "0001-01-01") return "N/A";
    
        // Parse the date string as UTC
        const utcDate = new Date(date);
    
        // Convert UTC to GMT (no timezone offset)
        const options = {
          timeZone: "UTC",       // GMT timezone
          dateStyle: "medium",   // Medium date format
          timeStyle: "short",    // Short time format
        };
    
        // Format the time in GMT
        const gmtTime = new Intl.DateTimeFormat("en-GB", options).format(utcDate);
    
        return gmtTime;
      },
      sorter: (a, b) =>
        new Date(a.orderDate).getTime() - new Date(b.orderDate).getTime(),
    }
    
    
    
,    
    { title: "Full Name", dataIndex: "fullName", key: "fullName" },
    { title: "Contact Number", dataIndex: "contactNumber", key: "contactNumber" },
    {
      title: "Payment Mode",
      dataIndex: "paymentMode",
      key: "paymentMode",
      render: (paymentMode) => paymentMode || "N/A",
    },
    {
      title: "Status",
      dataIndex: "orderCycle",
      key: "orderCycle",
      render: (orderCycle, record) => {
        const colors = {
          "Order Placement": "orange",
          Processing: "blue",
          Confirmed: "green",
          Pending: "yellow",
          Unreachable: "red",
          "Out of Stock": "red",
          "Wrong Number": "purple",
          Cancelled: "red",
          "Not Answered": "yellow",
          Delivery: "green",
          Completed: "lime",
        };
        return (
          <Tooltip title="Update order status">
            <Tag
              color={colors[orderCycle] || "default"}
              onClick={() => openCycleModal(record)}
              style={{ cursor: "pointer" }}
            >
              {orderCycle}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Tooltip title="View Order">
          <Button
            icon={<EyeOutlined style={{ color: "red" }} />}
            onClick={() => openDetailModal(record.orderCode)}
            shape="circle"
          />
        </Tooltip>
      ),
    },
  ];
 
  return (
    <div>
      <div className="text-2xl font-bold mb-4 text-red-500">Orders</div>
      <div className="flex justify-between items-center space-x-4 mb-4">
        <RangePicker
          format="MM-DD-YYYY"
          onChange={handleDateChange}
          className="w-full"
        />
        <Button
          onClick={handleFetchOrders}
          disabled={loading}
          className="w-auto bg-green-600 text-white"
        >
          Fetch Orders
        </Button>
      </div>
      <Search
        placeholder="Search by name or status"
        onSearch={handleSearch}
        className="w-full mb-4"
      />
     <div className="flex items-center justify-between mb-4">
  <div className="text-lg font-semibold">
    Total Filtered Orders: {filteredOrders.length}
  </div>
  <Button 
    onClick={exportToExcel} 
    className="bg-green-700 text-white px-4 py-2 rounded-md"
  >
    Export to Excel
  </Button>
</div>


      {loading ? (
  <div style={{
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "50vh", // Adjust height as needed to center vertically
    textAlign: "center",
  }}>
    
    <img 
      src={spin} 
      alt="Loading..." 
      style={{ width: "120px", height: "120px", marginBottom: "16px" }}
    />
    <div style={{
      fontSize: "1.2rem",
      fontWeight: "500",
      color: "#555",
    }}>
      Please wait...
    </div>
  </div>
     
      ) : filteredOrders.length > 0 ? (
        <Table
          dataSource={filteredOrders}
          columns={columns}
          rowKey="orderCode"
          pagination={pagination}
          onChange={handleTableChange}
        />
      ) : (
        <Empty description="No orders found" />
      )}
      <UpdateOrderCycleModal
        isVisible={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        orderCode={selectedOrderId}
        orderCycle={selectedOrderCycle}
        fetchOrders={fetchCurrentMonthOrders}
      />
      {isDetailModalOpen && (
        <OrderDetailsModal
          orderId={selectedOrderId}
          orderDetails={cachedOrderDetails[selectedOrderId]}
          onClose={() => setIsDetailModalOpen(false)}
        />
      )}
    </div>
  );
};

export default Orders;
